import React, { ReactNode, useEffect, useState } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Pagination, Paper, Stack, Typography, useTheme } from "@mui/material";
import { TextLoader } from "components/apiHandling/TextLoader";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import APIRequest from "system/APIRequest";
import ResourceChangeLog, { HistoryResponse, ResourceChange, ResourceType } from "./ChangeLog";
import DashboardDetails from "./headers/DashboardChangeLog";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);

interface ResourceHistoryDialogProps {
  onClose(): void;
  resourceId: string;
  resourceType: ResourceType;
}

export default function (props: ResourceHistoryDialogProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [history, setHistory] = useState<HistoryResponse>([]);  

  async function load(resourceId: string): Promise<void> {
    setIsLoading(true);

    const response = await APIRequest(`obj/history/${resourceId}`, undefined, undefined, true) as HistoryResponse;

    setHistory(response);
    setIsLoading(false);
  }

  function getDetailsComponent(id: string, type: ResourceType, lastChange: ResourceChange): ReactNode {
    switch (type) {
      case "dashboard": {
        return <DashboardDetails dashboardId={id} lastChange={lastChange} />
      }
      default: {
        return <div>Resource</div>
      }
    }
  }

  useEffect(() => {
    load(props.resourceId);
  }, []);

  const lastChange = history.length ? history[history.length-1]: null;

  return (
    <ResponsiveDialog 
      open={true}
      maxWidth="sm"
    >
      <DialogTitle>
        {isLoading &&
          <Typography variant="h4">{i18n.t("change-log-dialog.default-title")}</Typography>
        }
        {lastChange &&
          getDetailsComponent(props.resourceId, props.resourceType, lastChange)
        }
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          p: 0
        }}
      >
        {isLoading &&
          <Box sx={{alignSelf: "center", p: 5}}>
            <TextLoader />
          </Box>
        }
        {!isLoading &&
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              gap: 1,
            }}
          >
            <ResourceChangeLog history={history} /> 
          </Box>
        }
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          onClick={() => props.onClose()}
          variant="contained"
        >
          {i18n.t("close")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}
